/**
 * Public fee calculator: payout and fee estimates across supported marketplaces.
 * Formulas match SDK logic (Grailed, StockX, Mercari, Depop, eBay, Poshmark) for US sellers.
 */

export type MarketplaceKey =
  | 'GRAILED'
  | 'STOCKX'
  | 'MERCARI'
  | 'DEPOP'
  | 'EBAY'
  | 'POSHMARK';

export type FeeBreakdownDetails = Record<string, number>;

export interface MarketplaceFeeResult {
  marketplace: MarketplaceKey;
  name: string;
  fee: number;
  payout: number;
  breakdown?: string;
  breakdownDetails?: FeeBreakdownDetails;
}

export type GrailedPaymentType =
  | 'US_DOMESTIC'
  | 'INTERNATIONAL_ONBOARDED'
  | 'INTERNATIONAL_NOT_ONBOARDED'
  | 'NOT_STRIPE_ELIGIBLE';

export const GRAILED_PAYMENT_OPTIONS: {
  value: GrailedPaymentType;
  label: string;
  processorPct: number;
  processorFlat: number;
}[] = [
  { value: 'US_DOMESTIC', label: 'US domestic', processorPct: 3.49, processorFlat: 0.49 },
  { value: 'INTERNATIONAL_ONBOARDED', label: 'International (Stripe onboarded)', processorPct: 4.99, processorFlat: 0.49 },
  { value: 'INTERNATIONAL_NOT_ONBOARDED', label: 'International (not onboarded)', processorPct: 5.49, processorFlat: 0.49 },
  { value: 'NOT_STRIPE_ELIGIBLE', label: 'Not Stripe eligible country', processorPct: 4.99, processorFlat: 0.49 },
];

function calcGrailed(sellPrice: number, paymentType: GrailedPaymentType = 'US_DOMESTIC'): MarketplaceFeeResult {
  const transactionPct = 0.09;
  const transactionFee = +(sellPrice * transactionPct).toFixed(2);
  const option = GRAILED_PAYMENT_OPTIONS.find(o => o.value === paymentType) || GRAILED_PAYMENT_OPTIONS[0];
  const processorFee = +(sellPrice * (option.processorPct / 100) + option.processorFlat).toFixed(2);
  const fee = +(transactionFee + processorFee).toFixed(2);
  const payout = +(sellPrice - fee).toFixed(2);
  return {
    marketplace: 'GRAILED',
    name: 'Grailed',
    fee,
    payout,
    breakdown: `9% transaction ($${transactionFee}) + ${option.processorPct}% + $${option.processorFlat.toFixed(2)} payment processing ($${processorFee})`,
    breakdownDetails: { transactionFee: +transactionFee.toFixed(2), paymentProcessingFee: +processorFee.toFixed(2) },
  };
}

export const STOCKX_SELLER_LEVELS: { level: number; transactionPct: number }[] = [
  { level: 1, transactionPct: 9.0 },
  { level: 2, transactionPct: 8.5 },
  { level: 3, transactionPct: 8.0 },
  { level: 4, transactionPct: 7.5 },
  { level: 5, transactionPct: 7.0 },
];

function calcStockX(sellPrice: number, sellerLevel: number = 1): MarketplaceFeeResult {
  const levelInfo = STOCKX_SELLER_LEVELS.find(l => l.level === sellerLevel) || STOCKX_SELLER_LEVELS[0];
  const transactionPct = levelInfo.transactionPct / 100;
  const shippingFlat = 4;
  const processingFee = sellPrice * 0.03;
  const transactionFee = sellPrice * transactionPct;
  const raw = processingFee + transactionFee + shippingFlat;
  const fee = +Math.max(raw, 5).toFixed(2);
  const payout = +(sellPrice - fee).toFixed(2);
  return {
    marketplace: 'STOCKX',
    name: 'StockX',
    fee,
    payout,
    breakdown: `Level ${levelInfo.level}: 3% processing ($${processingFee.toFixed(2)}) + ${levelInfo.transactionPct}% transaction ($${transactionFee.toFixed(2)}) + $4 shipping (min $5 total)`,
    breakdownDetails: { processingFee: +processingFee.toFixed(2), transactionFee: +transactionFee.toFixed(2), shippingFee: shippingFlat, totalFee: fee },
  };
}

function calcMercari(sellPrice: number): MarketplaceFeeResult {
  const fee = +(sellPrice * 0.1).toFixed(2);
  const payout = +(sellPrice - fee).toFixed(2);
  return { marketplace: 'MERCARI', name: 'Mercari', fee, payout, breakdown: '10% selling fee', breakdownDetails: { sellingFee: fee } };
}

export type DepopRegion = 'US' | 'UK' | 'OTHER';

export const DEPOP_REGIONS: { value: DepopRegion; label: string }[] = [
  { value: 'US', label: 'US' },
  { value: 'UK', label: 'UK' },
  { value: 'OTHER', label: 'Other' },
];

function calcDepop(sellPrice: number, region: DepopRegion = 'US', boosted: boolean = false): MarketplaceFeeResult {
  const marketplaceFee = +(sellPrice * 0.1).toFixed(2);
  let processorFee = 0;
  const breakdownParts: string[] = [`10% selling fee ($${marketplaceFee.toFixed(2)})`];
  if (region === 'US') {
    processorFee = +(sellPrice * 0.033 + 0.45).toFixed(2);
    breakdownParts.push(`3.3% + $0.45 payment processing ($${processorFee.toFixed(2)})`);
  } else if (region === 'UK') {
    processorFee = +(sellPrice * 0.029 + 0.3).toFixed(2);
    breakdownParts.push(`2.9% + £0.30 payment processing ($${processorFee.toFixed(2)})`);
  } else {
    processorFee = +(sellPrice * 0.029 + 0.3).toFixed(2);
    breakdownParts.push(`2.9% + $0.30 processor ($${processorFee.toFixed(2)})`);
  }
  const boostFee = boosted ? +(sellPrice * 0.08).toFixed(2) : 0;
  if (boostFee > 0) breakdownParts.push(`8% boost ($${boostFee.toFixed(2)})`);
  const fee = +(marketplaceFee + processorFee + boostFee).toFixed(2);
  const payout = +(sellPrice - fee).toFixed(2);
  return {
    marketplace: 'DEPOP',
    name: 'Depop',
    fee,
    payout,
    breakdown: `${region} · ${breakdownParts.join('; ')}`,
    breakdownDetails: { sellingFee: marketplaceFee, paymentProcessingFee: processorFee, ...(boostFee > 0 && { boostFee }) },
  };
}

export type EbayCategoryType =
  | 'MOST_CATEGORIES'
  | 'SNEAKERS_OVER_150'
  | 'GUITARS_BASSES'
  | 'TRADING_CARDS'
  | 'HEAVY_EQUIPMENT'
  | 'BOOKS_MOVIES_MUSIC'
  | 'BULLION'
  | 'MOST_JEWELRY_WATCHES'
  | 'WATCHES_PARTS_ACCESSORIES'
  | 'NFTS'
  | 'WOMENS_BAGS_HANDBAGS';

export type EbayCategoryOption = {
  value: EbayCategoryType;
  label: string;
  pctUpTo: number;
  pctOver: number;
  threshold: number;
  flatPct?: number;
  threshold2?: number;
  pctMiddle?: number;
  noPerOrderFee?: boolean;
};

export const EBAY_CATEGORY_OPTIONS: EbayCategoryOption[] = [
  { value: 'MOST_CATEGORIES', label: 'Most categories', pctUpTo: 13.6, pctOver: 2.35, threshold: 7500 },
  { value: 'SNEAKERS_OVER_150', label: 'Sneakers over $150', pctUpTo: 8, pctOver: 8, threshold: 0, flatPct: 8, noPerOrderFee: true },
  { value: 'GUITARS_BASSES', label: 'Guitars & Basses', pctUpTo: 6.7, pctOver: 2.35, threshold: 7500 },
  { value: 'TRADING_CARDS', label: 'Trading cards', pctUpTo: 13.25, pctOver: 2.35, threshold: 7500 },
  { value: 'HEAVY_EQUIPMENT', label: 'Heavy equipment', pctUpTo: 3, pctOver: 0.5, threshold: 15000 },
  { value: 'BOOKS_MOVIES_MUSIC', label: 'Books, Movies & Music', pctUpTo: 15.3, pctOver: 2.35, threshold: 7500 },
  { value: 'BULLION', label: 'Bullion', pctUpTo: 13.6, pctOver: 7, threshold: 7500 },
  { value: 'MOST_JEWELRY_WATCHES', label: 'Most Jewelry & Watches', pctUpTo: 15, pctOver: 9, threshold: 5000 },
  { value: 'WATCHES_PARTS_ACCESSORIES', label: 'Watches, Parts & Accessories', pctUpTo: 15, pctOver: 3, threshold: 1000, threshold2: 7500, pctMiddle: 6.5 },
  { value: 'NFTS', label: 'NFTs', pctUpTo: 5, pctOver: 5, threshold: 0, flatPct: 5 },
  { value: 'WOMENS_BAGS_HANDBAGS', label: "Women's Bags & Handbags", pctUpTo: 15, pctOver: 9, threshold: 2000 },
];

function calcEbay(sellPrice: number, categoryType: EbayCategoryType = 'MOST_CATEGORIES'): MarketplaceFeeResult {
  const option = EBAY_CATEGORY_OPTIONS.find(o => o.value === categoryType) || EBAY_CATEGORY_OPTIONS[0];
  const { pctUpTo, pctOver, threshold, flatPct, threshold2, pctMiddle, noPerOrderFee } = option;
  let fvf: number;
  let breakdown: string;
  if (flatPct !== undefined) {
    fvf = sellPrice * (flatPct / 100);
    breakdown = `${flatPct}% final value fee`;
  } else if (threshold2 !== undefined && pctMiddle !== undefined && threshold > 0) {
    const amt1 = Math.min(sellPrice, threshold);
    const amt2 = Math.min(Math.max(0, sellPrice - threshold), threshold2 - threshold);
    const amt3 = Math.max(0, sellPrice - threshold2);
    fvf = amt1 * (pctUpTo / 100) + amt2 * (pctMiddle / 100) + amt3 * (pctOver / 100);
    breakdown = `${pctUpTo}% on first $${threshold.toLocaleString()}`;
    if (amt2 > 0) breakdown += `, ${pctMiddle}% on $${threshold.toLocaleString()}–$${threshold2.toLocaleString()} ($${amt2.toFixed(2)})`;
    if (amt3 > 0) breakdown += `, ${pctOver}% on remainder ($${amt3.toFixed(2)})`;
  } else {
    const amountUpTo = Math.min(sellPrice, threshold || sellPrice);
    const amountOver = threshold > 0 ? Math.max(0, sellPrice - threshold) : 0;
    fvf = amountUpTo * (pctUpTo / 100) + amountOver * (pctOver / 100);
    breakdown = threshold > 0 && amountOver > 0 ? `${pctUpTo}% on first $${threshold.toLocaleString()} ($${amountUpTo.toFixed(2)}) + ${pctOver}% on remainder ($${amountOver.toFixed(2)})` : `${pctUpTo}% final value fee`;
  }
  const perOrderFee = noPerOrderFee || sellPrice <= 0 ? 0 : sellPrice <= 10 ? 0.3 : 0.4;
  const perOrderText = noPerOrderFee ? ' (no per order fee)' : sellPrice <= 10 ? ' + $0.30 per order' : ' + $0.40 per order';
  const fee = +(fvf + perOrderFee).toFixed(2);
  const payout = +(sellPrice - fee).toFixed(2);
  return { marketplace: 'EBAY', name: 'eBay', fee, payout, breakdown: breakdown + perOrderText, breakdownDetails: { finalValueFee: +fvf.toFixed(2), perOrderFee, totalFee: fee } };
}

function calcPoshmark(sellPrice: number): MarketplaceFeeResult {
  const fee = sellPrice < 15 ? 2.95 : +(sellPrice * 0.2).toFixed(2);
  const payout = +(sellPrice - fee).toFixed(2);
  const breakdown = sellPrice < 15 ? 'Under $15: $2.95 flat fee' : '$15 and above: 20% seller fee';
  return { marketplace: 'POSHMARK', name: 'Poshmark', fee, payout, breakdown, breakdownDetails: { sellerFee: fee } };
}

export type FeeCalculatorOptions = {
  stockxSellerLevel?: number;
  depopRegion?: DepopRegion;
  depopBoosted?: boolean;
  grailedPaymentType?: GrailedPaymentType;
  ebayCategoryType?: EbayCategoryType;
};

const CALCULATORS: Record<MarketplaceKey, (price: number, options?: FeeCalculatorOptions) => MarketplaceFeeResult> = {
  GRAILED: (p, opts) => calcGrailed(p, opts?.grailedPaymentType ?? 'US_DOMESTIC'),
  STOCKX: (p, opts) => calcStockX(p, opts?.stockxSellerLevel ?? 1),
  MERCARI: p => calcMercari(p),
  DEPOP: (p, opts) => calcDepop(p, opts?.depopRegion ?? 'US', opts?.depopBoosted ?? false),
  EBAY: (p, opts) => calcEbay(p, opts?.ebayCategoryType ?? 'MOST_CATEGORIES'),
  POSHMARK: p => calcPoshmark(p),
};

export function calculateAllMarketplaceFees(sellPrice: number, options?: FeeCalculatorOptions): MarketplaceFeeResult[] {
  if (!Number.isFinite(sellPrice) || sellPrice < 0) return [];
  const results = (Object.keys(CALCULATORS) as MarketplaceKey[]).map(key => CALCULATORS[key](sellPrice, options));
  return results.sort((a, b) => b.payout - a.payout);
}
